import { Component, Input, OnInit, OnDestroy, EventEmitter, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Domain } from '@monsido/ng2/modules/models/api/domain';
import { DomainGroupRepoService } from '@client/app/services/api/domain-groups-repo/domain-group-repo.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DomainGroupService } from '@client/app/services/entities/domain-group/domain-group.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { DomainGroup } from '@monsido/modules/models/api/domain-group';
import { FormDomainGroupsCreateComponent } from '@client/app/forms/domain/steps/domain_groups/create/create.component';
import {
    FormDomainGroupsEditComponent,
} from '@client/app/forms/domain/steps/domain_groups/edit/form-domain-groups-edit.component';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'form-domain-groups',
    templateUrl: './form-domain-groups.component.html',
    standalone: false,

})
export class FormDomainGroupsComponent extends BaseContainerComponentController implements OnInit, OnDestroy, OnChanges {
    @Input() domain!: Domain;
    @Input() progress!: Promise<any>;

    @Output() updateDomain = new EventEmitter();

    groups: any[] = [];
    promise: Promise<any> | null = null;
    timerPromise: any = null;
    override page = 1;
    override pageSize = 10;

    constructor (
        private domainGroupRepoService: DomainGroupRepoService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
        private domainGroupService: DomainGroupService,
        private monEventService: MonEventService,
    ) {
        super({ table: true });
    }

    ngOnInit (): void {
        this.init();
    }

    ngOnChanges (changes: SimpleChanges): void {
        // Re-fetch groups when parent refreshes domain, so group.updating flags stay current
        if (changes['domain'] && !changes['domain'].firstChange) {
            this.getPage();
        }
        // Stop polling once server confirms update is complete
        if (this.domain.running_groups === false) {
            this.onDestroy();
        }
    }

    ngOnDestroy (): void {
        this.onDestroy();
    }

    private init (): void {
        this.loadLocations();

        if (this.progress) {
            this.progress.then(() => {
                setTimeout(() => {
                    if (this.domain.running_groups) {
                        this.autoReloadDomain();
                    }
                });
            });
        }
    }

    private onDestroy (): void {
        if (this.timerPromise) {
            clearInterval(this.timerPromise);
        }
    }

    private loadLocations (): void {
        this.getPage();
    }

    override getPage = (): void => {
        const params = {
            page: this.page,
            page_size: this.pageSize,
        };

        this.promise = this.domainGroupRepoService.getAll(this.domain.id, params)
            .then((data: any) => {
                this.groups = data.data || data as any;
                // Safety net: clear stale running_groups flag if all groups have finished updating
                if (this.domain.running_groups && !this.groups.some((g: any) => g.updating)) {
                    this.domain.running_groups = false;
                    this.onDestroy();
                }
                this.updateDropdowns();
            })
            .catch(() => {
                // Handle error silently
            });
    };

    private updateDropdowns (): void {
        for (const group of this.groups) {
            group.dropdown = [
                {
                    label: this.translateService.getString('Edit'),
                    leftIcon: 'faCog',
                    action: () => this.editGroup(group),
                },
                {
                    label: this.translateService.getString('Remove group'),
                    leftIcon: 'faTrashAlt',
                    action: () => {
                        this.monModalService.confirm(
                            this.translateService.getString('Are you sure you want to remove this group?'),
                        ).then(() => this.onConfirmDelete(group));
                    },
                },
            ];
        }
    }

    editGroup (group: any): void {
        const params = {
            body: FormDomainGroupsEditComponent,
            size: 'lg',
            data: { domainGroup: group, domain: this.domain },
        };

        const options = {
            params,
            callback: (content: any) => {
                if (content !== undefined) {
                    this.getPage();
                    this.autoUpdate();
                }
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    createGroup (): void {
        const params = {
            body: FormDomainGroupsCreateComponent,
            size: 'lg',
            data: { domain: this.domain },
        };

        const options = {
            params,
            callback: (content: any) => {
                if (content !== undefined) {
                    this.getPage();
                    this.autoUpdate();
                }
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    private autoUpdate (): void {
        this.monModalService
            .confirm(
                this.translateService.getString(
                    'Would you like to update now? Updating groups can take more than 30 minutes. During the operation you will not be able to create and update groups',
                ),
            )
            .then(() => {
                this.updateDomainGroupsRules();
            })
            .catch(() => {
                // Handle rejection silently
            });
    }

    updateDomainGroupsRules (): void {
        this.domainGroupService.updateDomainGroupRules(this.domain.id)
            .then(() => {
                this.domain.running_groups = true;
                this.autoReloadDomain();
            })
            .catch(() => {
                // Handle error silently
            });
    }

    // Polls domain + groups every 10s while update is in progress
    private autoReloadDomain (): void {
        this.timerPromise = setInterval(() => {
            if (this.updateDomain) {
                this.updateDomain.emit();
            }
        }, 10000);
    }

    private onConfirmDelete (group: DomainGroup): void {
        this.domainGroupService.destroy(this.domain.id, group.id)
            .then(() => {
                this.getPage();
            })
            .catch(() => {
                // Handle error silently
            });
    }
}
